/**
 * Announcement Service
 * 
 * Firestore service for managing site-wide announcement popups.
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  increment,
} from 'firebase/firestore';
import { db } from './firebase';
import { Announcement, AnnouncementFormData, getAnnouncementStatus, ANNOUNCEMENT_SHOW_DELAY_MS } from '@/types/announcements';

const COLLECTION_NAME = 'announcements';

class AnnouncementService {
  private getCollection() {
    if (!db) {
      console.warn('[AnnouncementService] Firestore not initialized');
      return null;
    }
    return collection(db, COLLECTION_NAME);
  }

  /**
   * Get all announcements
   */
  async getAllAnnouncements(): Promise<Announcement[]> {
    const col = this.getCollection();
    if (!col) return [];

    try {
      const q = query(col, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Announcement));
    } catch (error) {
      console.error('[AnnouncementService] Failed to fetch announcements:', error);
      return [];
    }
  }

  /**
   * Get the currently active announcement (enabled, within date range)
   * Only returns one - the most recently created active one
   */
  async getActiveAnnouncement(): Promise<Announcement | null> {
    const col = this.getCollection();
    if (!col) return null;

    try {
      const q = query(col, where('enabled', '==', true));
      const snapshot = await getDocs(q);
      
      // Filter to only active status and get the most recent
      const activeAnnouncements = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Announcement))
        .filter(announcement => getAnnouncementStatus(announcement) === 'active')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      return activeAnnouncements[0] || null;
    } catch (error) {
      console.error('[AnnouncementService] Failed to fetch active announcement:', error);
      return null;
    }
  }

  /**
   * Check if there's already an active announcement (for validation)
   */
  async hasActiveAnnouncement(excludeId?: string): Promise<boolean> {
    const active = await this.getActiveAnnouncement();
    if (!active) return false;
    if (excludeId && active.id === excludeId) return false;
    return true;
  }

  /**
   * Get a single announcement by ID
   */
  async getAnnouncement(id: string): Promise<Announcement | null> {
    if (!db) return null;

    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) return null;
      
      return { id: docSnap.id, ...docSnap.data() } as Announcement;
    } catch (error) {
      console.error('[AnnouncementService] Failed to fetch announcement:', error);
      return null;
    }
  }

  /**
   * Create a new announcement
   * Note: Firestore doesn't allow undefined values, so we filter them out
   */
  async createAnnouncement(data: AnnouncementFormData, userId?: string): Promise<string | null> {
    const col = this.getCollection();
    if (!col) return null;

    try {
      const now = new Date().toISOString();
      
      // Build object without undefined values (Firestore doesn't allow undefined)
      const announcementData: Record<string, unknown> = {
        title: data.title,
        bulletPoints: data.bulletPoints || [],
        ctaText: data.ctaText,
        ctaUrl: data.ctaUrl,
        icon: data.icon,
        accentColor: data.accentColor,
        enabled: data.enabled,
        endDate: data.endDate,
        createdAt: now,
        updatedAt: now,
        impressions: 0,
        clicks: 0,
      };

      // Only add optional fields if they have values
      if (data.subtitle) announcementData.subtitle = data.subtitle;
      if (data.dismissText) announcementData.dismissText = data.dismissText;
      if (data.startDate) announcementData.startDate = data.startDate;
      if (userId) announcementData.createdBy = userId;

      const docRef = await addDoc(col, announcementData);
      return docRef.id;
    } catch (error) {
      console.error('[AnnouncementService] Failed to create announcement:', error);
      return null;
    }
  }

  /**
   * Update an existing announcement
   */
  async updateAnnouncement(id: string, data: Partial<AnnouncementFormData>): Promise<boolean> {
    if (!db) return false;

    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date().toISOString(),
      });
      return true;
    } catch (error) {
      console.error('[AnnouncementService] Failed to update announcement:', error);
      return false;
    }
  }

  /**
   * Delete an announcement
   */
  async deleteAnnouncement(id: string): Promise<boolean> {
    if (!db) return false;

    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('[AnnouncementService] Failed to delete announcement:', error);
      return false;
    }
  }

  /**
   * Track an impression for an announcement
   * Uses atomic increment to avoid race conditions with concurrent users
   */
  async trackImpression(id: string): Promise<void> {
    if (!db) return;

    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        impressions: increment(1),
      });
    } catch (error) {
      // Silent fail for analytics
    }
  }

  /**
   * Track a click for an announcement
   * Uses atomic increment to avoid race conditions with concurrent users
   */
  async trackClick(id: string): Promise<void> {
    if (!db) return;

    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        clicks: increment(1),
      });
    } catch (error) {
      // Silent fail for analytics
    }
  }
}

export const announcementService = new AnnouncementService();

